import { DocumentType, type BaseDocument, type DocumentSource } from '@/types'
import {
AppstoreOutlined,
BgColorsOutlined,
BorderOuterOutlined,
FileTextOutlined,
NodeIndexOutlined,
ProjectOutlined,
} from '@ant-design/icons'
import { createElement,type ReactNode } from 'react'

export const DOCUMENT_TYPE_ORDER = [
  DocumentType.TEXT,
  DocumentType.WHITEBOARD,
  DocumentType.MINDMAP,
  DocumentType.KANBAN,
  DocumentType.COMPOSITE,
  DocumentType.INFINITE,
] as const

export const DOCUMENT_TYPE_LABELS: Record<DocumentType, string> = {
  [DocumentType.TEXT]: '文本笔记',
  [DocumentType.WHITEBOARD]: '白板',
  [DocumentType.MINDMAP]: '思维导图',
  [DocumentType.KANBAN]: '看板',
  [DocumentType.COMPOSITE]: '混排文档',
  [DocumentType.INFINITE]: '无限',
}

export const DOCUMENT_TYPE_COLORS: Record<DocumentType, string> = {
  [DocumentType.TEXT]: '#1890ff',
  [DocumentType.WHITEBOARD]: '#52c41a',
  [DocumentType.MINDMAP]: '#722ed1',
  [DocumentType.KANBAN]: '#fa8c16',
  [DocumentType.COMPOSITE]: '#eb2f96',
  [DocumentType.INFINITE]: '#13c2c2',
}

const DOCUMENT_TYPE_CREATE_PATHS: Record<DocumentType, string> = {
  [DocumentType.TEXT]: '/editor',
  [DocumentType.WHITEBOARD]: '/whiteboard',
  [DocumentType.MINDMAP]: '/mindmap',
  [DocumentType.KANBAN]: '/kanban',
  [DocumentType.COMPOSITE]: '/composite',
  [DocumentType.INFINITE]: '/infinite',
}

const DOCUMENT_TYPE_ICON_RENDERERS: Record<DocumentType, (className?: string) => ReactNode> = {
  [DocumentType.TEXT]: className => createElement(FileTextOutlined, { className }),
  [DocumentType.WHITEBOARD]: className => createElement(BgColorsOutlined, { className }),
  [DocumentType.MINDMAP]: className => createElement(NodeIndexOutlined, { className }),
  [DocumentType.KANBAN]: className => createElement(ProjectOutlined, { className }),
  [DocumentType.COMPOSITE]: className => createElement(AppstoreOutlined, { className }),
  [DocumentType.INFINITE]: className => createElement(BorderOuterOutlined, { className }),
}

export const getDocumentTypeLabel = (type: DocumentType): string => {
  return DOCUMENT_TYPE_LABELS[type]
}

export const getDocumentTypeColor = (type: DocumentType): string => {
  return DOCUMENT_TYPE_COLORS[type]
}

export const getDocumentCreatePath = (type: DocumentType): string => {
  return DOCUMENT_TYPE_CREATE_PATHS[type]
}

export const getDocumentEditPath = (type: DocumentType, documentId: string): string => {
  return `${getDocumentCreatePath(type)}/${documentId}`
}

export const renderDocumentTypeIcon = (type: DocumentType, className?: string): ReactNode => {
  return DOCUMENT_TYPE_ICON_RENDERERS[type](className)
}

// ---------- 文档来源（native/plain）标识 ----------

export const SOURCE_LABELS: Record<DocumentSource, string> = {
  native: 'mdn',
  plain: '常规',
}

export const SOURCE_COLORS: Record<DocumentSource, string> = {
  native: '#1890ff',
  plain: '#8c8c8c',
}

/** 获取文档来源标签（undefined 视为 native） */
export const getSourceLabel = (doc: Pick<BaseDocument, 'source'>): string => {
  return SOURCE_LABELS[doc.source ?? 'native']
}

/** 获取文档来源颜色 */
export const getSourceColor = (doc: Pick<BaseDocument, 'source'>): string => {
  return SOURCE_COLORS[doc.source ?? 'native']
}

/** 是否为 plain 文件 */
export const isPlainDocument = (doc: Pick<BaseDocument, 'source'>): boolean => {
  return doc.source === 'plain'
}

/**
 * plain 文件按 MIME 给显示标签（替代失真的 kind=TEXT）
 * 用于 FreeBlock 标题条等需要显示块类型的场景
 */
export const getPlainKindLabel = (
  doc: Pick<BaseDocument, 'source' | 'originalMimeType' | 'isDirectory'>
): string | null => {
  if (doc.source !== 'plain') return null
  if (doc.isDirectory) return '目录'
  const mime = (doc.originalMimeType ?? '').toLowerCase()
  if (!mime) return '文件'
  if (mime.startsWith('image/')) return '图片'
  if (mime === 'application/pdf') return 'PDF'
  if (mime.startsWith('video/')) return '视频'
  if (mime.startsWith('audio/')) return '音频'
  if (/^text\/(plain|markdown|html|csv|css|javascript|typescript|x-yaml|xml)|^(application\/(json|yaml|x-yaml|xml))/.test(mime)) {
    return '文本'
  }
  return '文件'
}

/**
 * plain 文件按 MIME 给默认尺寸（图片 16:9，PDF 竖版，音视频 16:9，文本默认）
 */
export const getPlainDefaultSize = (
  doc: Pick<BaseDocument, 'source' | 'originalMimeType' | 'isDirectory'>
): { w: number; h: number } | null => {
  if (doc.source !== 'plain') return null
  if (doc.isDirectory) return { w: 280, h: 120 }
  const mime = (doc.originalMimeType ?? '').toLowerCase()
  if (!mime) return null
  if (mime.startsWith('image/')) return { w: 640, h: 400 }
  if (mime === 'application/pdf') return { w: 520, h: 680 }
  if (mime.startsWith('video/')) return { w: 640, h: 400 }
  if (mime.startsWith('audio/')) return { w: 480, h: 120 }
  return null
}
